import React from 'react';

import errorKnockoutTokens from '@amzn/meridian-tokens/base/icon/error-knockout';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { SUBMIT_FEATURE_REQUEST_AND_BUG_URL } from 'src/config/ExternalLinks';
import type { ModifiedDutyCountrySummary } from 'src/services/associateProfile/types';

interface ModifiedDutyBreakdownModalProps {
  unknownDutyDays?: number;
  countrySummary?: ModifiedDutyCountrySummary[];
  isOpen: boolean;
  closeModal: () => void;
}

const ModifiedDutyBreakdownModal = ({
  isOpen,
  closeModal,
  unknownDutyDays,
  countrySummary,
}: ModifiedDutyBreakdownModalProps) => (
  <Modal
    width="680px"
    bodySpacingInset="400"
    title="Modified Duty Counter"
    open={isOpen}
    onClose={closeModal}
    scrollContainer="modal"
  >
    <Column spacing="400">
      <Text type="b300">
        Overlapping days are not double counted, therefore the total modified duty for a country may not equal the sum
        of the modified duty count across sites.
      </Text>

      {unknownDutyDays ? (
        <Alert type="error" iconTokens={errorKnockoutTokens}>
          <Text type="b200">
            Unknown days identified. Please submit a&nbsp;
            <Link href={SUBMIT_FEATURE_REQUEST_AND_BUG_URL} target="_blank" rel="noopener noreferrer">
              SIM ticket
            </Link>
            .
          </Text>
        </Alert>
      ) : null}

      {countrySummary?.map((cs) => {
        const unknownDutyDaysExist = cs.unknownDutyDays > 0;
        return (
          <Column key={cs.countryId} spacing="200">
            <Text type="h200">
              Total Modified Duty Across {cs.countryName} Sites = {cs.modifiedDutyDays} Days
            </Text>
            <DataTable size="large" showBorder={true}>
              <DataTableHead>
                <DataTableRow>
                  <DataTableHeader textAlign="start">
                    <Text type="h100">Site</Text>
                  </DataTableHeader>
                  <DataTableHeader textAlign="start">
                    <Text type="h100">Modified Duty Count</Text>
                  </DataTableHeader>
                  {unknownDutyDaysExist && (
                    <DataTableHeader textAlign="start">
                      <Text type="h100" color="error">
                        Unknown Duty Count
                      </Text>
                    </DataTableHeader>
                  )}
                  <DataTableHeader textAlign="start">
                    <Text type="h100">Process Path Count</Text>
                  </DataTableHeader>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {[...(cs.sites ?? [])]
                  .sort((a, b) => a.site.localeCompare(b.site))
                  .map((site) => (
                    <DataTableRow key={site.siteId}>
                      <DataTableCell>
                        <Text type="b300">{site.site}</Text>
                      </DataTableCell>
                      <DataTableCell>
                        <Text type="b300">{site.modifiedDutyDays}</Text>
                      </DataTableCell>
                      {unknownDutyDaysExist && (
                        <DataTableCell>
                          <Text type="b300" color="error">
                            {site.unknownDutyDays}
                          </Text>
                        </DataTableCell>
                      )}
                      <DataTableCell>
                        <Text type="b300">{site.processPathDays}</Text>
                      </DataTableCell>
                    </DataTableRow>
                  ))}
              </DataTableBody>
            </DataTable>
          </Column>
        );
      })}
    </Column>
    <ModalFooter>
      <Row alignmentHorizontal="end">
        <Button size="medium" onClick={closeModal}>
          Close
        </Button>
      </Row>
    </ModalFooter>
  </Modal>
);

export default ModifiedDutyBreakdownModal;
